import type { ContentHref, ContentKey } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { extractContentAndRelations } from '@newStore/documentApi/documentApiHelpers';
import { arrayToObjectMap, parentChildRelationFilter } from '@newStore/genericHelpers';
import { referenceFramesByHref } from '@newStore/referenceFrames/referenceFrames';
import type { PayloadAction } from '@reduxjs/toolkit';
import { addNotificationAction } from '@store/actions/notificationActions';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { getCachedDocumentByRoot, getHashByRoot } from '../documentApi/documentApiDataAccess';
import { selectIsReferenceFrameLoaded } from './referenceFramesSelectors';
import { loadReferenceFrame, loadReferenceFrameSuccess } from './referenceFramesState';

function* fetchReferenceFrame({
  payload: { referenceFrame },
}: PayloadAction<{ referenceFrame: ContentHref }>) {
  try {
    const key = referenceFrame.split('/').pop() as ContentKey;
    const isReferenceFrameLoaded: boolean = yield select((state: RootState) =>
      selectIsReferenceFrameLoaded(state, referenceFrame),
    );

    if (isReferenceFrameLoaded) {
      const currentHash: string | null = yield select(
        (state: RootState) => state.referenceFrames[referenceFrame]?.hash,
      );
      const latestHash: string = yield call(getHashByRoot, key);
      if (currentHash === latestHash) {
        return;
      }
    }

    const { results: contentApiResults, hash } = yield call(getCachedDocumentByRoot, key);
    const { content: contentArray, relations: apiRelations } =
      extractContentAndRelations(contentApiResults);
    const content = arrayToObjectMap(contentArray);
    const relations = apiRelations.filter(parentChildRelationFilter);
    yield put(loadReferenceFrameSuccess({ referenceFrame, content, relations, hash }));
  } catch (error) {
    console.error(
      `error fetching ${referenceFramesByHref[referenceFrame]} - ${referenceFrame} reference frame`,
    );
    yield put(
      addNotificationAction({
        type: 'ERROR',
        message: `Er is een onverwachte fout opgetreden bij het ophalen van het ${referenceFramesByHref[referenceFrame]} ordeningskader.'`,
      }),
    );
  }
}

export function* referenceFramesSaga() {
  yield takeEvery(loadReferenceFrame.match, fetchReferenceFrame);
}
